import { useEffect, useMemo, useState } from 'react';
import MigrationItemViewDTO from '@src/adapters/dto/migration/MigrationItemViewDTO';
import { Box, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CardItem from './CardItem';
import MigrationItemUpdateDTO from '@src/adapters/dto/migration/MigrationItemUpdateDTO';
import { MigrationStatus } from '@src/constants/MigrationStatus';

interface Props {
  items: MigrationItemViewDTO[];
  highlightedId?: string;
  lockView: Boolean;
  collapsed: Boolean;
  status: MigrationStatus;
  isOwner: Boolean;
  deleteItem(id: string): void;
  itemUpdated(item: MigrationItemUpdateDTO): void;
  highlightItem(suggestionId?: string): void;
  handleItemApprovalUpdate(
    itemId: string,
    suggestionId: string | undefined,
    approved: boolean
  ): void;
  onCategorySectionToggled(isExpanded: boolean): void;
}

function CategorySection({
  items,
  highlightedId,
  lockView,
  collapsed,
  status,
  isOwner,
  deleteItem,
  itemUpdated,
  highlightItem,
  handleItemApprovalUpdate,
  onCategorySectionToggled,
}: Props) {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setExpanded(!collapsed);
  }, [collapsed]);

  const sortedItems = useMemo(() => {
    const indexedItems = items.filter((i) => i.suggestion);
    const nonIndexedItems = items.filter((i) => !i.suggestion);

    const sortedItems = [
      ...indexedItems.sort((a, b) => a.suggestion!.index - b.suggestion!.index),
      ...nonIndexedItems.sort((a, b) =>
        a.inventory.name.localeCompare(b.inventory.name)
      ),
    ];

    return sortedItems;
  }, [items]);

  return (
    <>
      <Box
        sx={{
          height: '48px',
          display: 'flex',
          paddingLeft: '24px',
          paddingTop: '8px',
        }}>
        <Box sx={{ width: '350px' }}>
          {items && items[0] && (
            <Typography variant="h6">
              {items[0].category.name} (
              {status === MigrationStatus.InApproval
                ? `${items.filter((i) => i.approval).length}/${items.length}`
                : items.length}
              )
            </Typography>
          )}
        </Box>
        {expanded && (
          <ExpandLessIcon
            sx={(theme) => ({
              marginLeft: '16px',
              marginRight: '24px',
              width: '24px',
              height: '24px',
              color: theme.palette.action.active,
              cursor: 'pointer',
            })}
            onClick={() => {
              setExpanded(false);
              onCategorySectionToggled(false);
            }}
          />
        )}
        {!expanded && (
          <ExpandMoreIcon
            sx={(theme) => ({
              marginLeft: '16px',
              marginRight: '24px',
              color: theme.palette.action.active,
              cursor: 'pointer',
            })}
            onClick={() => {
              setExpanded(true);
              onCategorySectionToggled(true);
            }}
          />
        )}
      </Box>
      {expanded &&
        sortedItems.map((item) => (
          <CardItem
            item={item}
            isOwner={isOwner}
            highlightedId={highlightedId}
            itemUpdated={itemUpdated}
            deleteItem={deleteItem}
            highlightItem={highlightItem}
            handleApprovalUpdate={handleItemApprovalUpdate}
            lockView={lockView}
            key={item.id}
            status={status}
          />
        ))}
    </>
  );
}

export default CategorySection;
